// Datum och tid i notiser — EN källa, så regeln går att greppa och testa.
//
// BAKGRUNDEN (008 Fas 0): en notis vars body löd "…gick med i din aktivitet
// 16 jan. 06:00" visades bredvid relativtiden "ungefär en månad sedan". Det såg
// ut som ett sju månader fel klockslag. Ingen av uppgifterna var fel:
//   - relativtiden beskrev NOTISEN (created_at 2026-07-02) — korrekt
//   - "16 jan." beskrev AKTIVITETEN — och saknade årtal
// Aktivitetslistan hämtar bara framtida datum, så raden var 2027-01-16. Läsaren
// tolkade den som januari 2026, i det förflutna, och fick en skenbar motsägelse.
//
// REGELN som följer av det:
//   Ett datum som FRYSES i en notistext (den ligger kvar i DB i månader och läses
//   långt efter att den skrevs) måste ALLTID bära årtal.
//   Ett datum som RENDERAS LIVE ur en sorterad framtidslista behöver det inte —
//   där ger listan självt kontexten.
//
// Använd frozenDate/frozenDateTime för allt som skrivs till notifications.body.
// Skriv aldrig `format(..., "d MMM")` direkt i en notis-body igen.
import { format, parseISO } from "date-fns";
import { sv } from "date-fns/locale";

/**
 * Sträng eller Date → Date.
 *
 * Rena datumsträngar ("2027-01-16") går via parseISO, som ger LOKAL midnatt.
 * `new Date("2027-01-16")` hade gett UTC-midnatt och kan därmed landa på fel
 * dygn i negativa offset — helpern ska inte bete sig olika beroende på var den
 * körs.
 */
function toDate(value: Date | string): Date {
  return value instanceof Date ? value : parseISO(value);
}

/** Datum fryst i en notistext: "16 jan. 2027". Årtalet är obligatoriskt. */
export function frozenDate(value: Date | string): string {
  return format(toDate(value), "d MMM yyyy", { locale: sv });
}

/** Datum + klockslag fryst i en notistext: "16 jan. 2027 06:00". */
export function frozenDateTime(value: Date | string): string {
  return format(toDate(value), "d MMM yyyy HH:mm", { locale: sv });
}

/**
 * Notisens EGEN tidsstämpel som den visas i panelen.
 *
 * Panelen visade tidigare bara relativ tid. Två notiser skrevs då båda ut som
 * "för 3 timmar sedan" och blev omöjliga att skilja åt — vilket fick genuint
 * olika händelser att se ut som dubbletter.
 *
 * SEKUNDER, inte minuter. Det rapporterade paret ("Ny spelare i din aktivitet!")
 * ligger 43 sekunder isär INOM samma minut — 14:54:15 och 14:54:58. Med
 * minutupplösning renderas de fortfarande identiskt, och den rapporterade buggen
 * står kvar. Eftersom bodyn på gamla rader inte backfillas är tidsstämpeln det
 * ENDA som kan skilja dem åt, och då måste den vara exakt.
 *
 * Årtalet tas med först när notisen inte är från innevarande år: färska notiser
 * ska vara korta att läsa, gamla ska inte kunna missförstås.
 */
export function notificationTimestamp(value: Date | string, now: Date): string {
  const d = toDate(value);
  const pattern = d.getFullYear() === now.getFullYear() ? "d MMM HH:mm:ss" : "d MMM yyyy HH:mm:ss";
  return format(d, pattern, { locale: sv });
}

/**
 * Vilka notiser som ska markeras som lästa när panelen öppnas.
 *
 * Bara de rader som FAKTISKT hämtades och renderades — listan är begränsad
 * (limit 50) medan badgen räknar obegränsat, så en blind "markera allt" hade
 * markerat notiser användaren aldrig sett.
 */
export function unreadIdsToMark(rows: ReadonlyArray<{ id: string; read: boolean | null }>): string[] {
  return rows.filter((r) => !r.read).map((r) => r.id);
}
